import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, json } from '../api/client'
import type { EnhanceOptions, Sound } from '../api/types'
import { episodeKey } from './useShows'

const soundKey = (ws: string, id: string) => [...episodeKey(ws, id), 'sound'] as const

/** Where an episode's sound stands. Polls while a clean-up is running. */
export function useSound(ws: string, id: string) {
  return useQuery({
    queryKey: soundKey(ws, id),
    queryFn: () => api<Sound>(`/workspaces/${ws}/episodes/${id}/sound`),
    refetchInterval: (query) => (query.state.data?.running ? 2_000 : false),
  })
}

/** How much the cuts would take off, without running anything. */
export function usePreviewCuts(ws: string, id: string) {
  return useMutation({
    mutationFn: (options: EnhanceOptions) =>
      api<{ removing_ms: number }>(`/workspaces/${ws}/episodes/${id}/sound/preview`, { method: 'POST', body: json(options) }),
  })
}

function refresh(queryClient: ReturnType<typeof useQueryClient>, ws: string, id: string) {
  void queryClient.invalidateQueries({ queryKey: soundKey(ws, id) })
  void queryClient.invalidateQueries({ queryKey: episodeKey(ws, id) })
}

export function useEnhanceSound(ws: string, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (options: EnhanceOptions) =>
      api<{ job_id: string; removing_ms: number }>(`/workspaces/${ws}/episodes/${id}/sound`, { method: 'POST', body: json(options) }),
    onSuccess: () => refresh(queryClient, ws, id),
  })
}

/** Choose whether listeners hear the cleaned audio or the original. */
export function useChooseAudio(ws: string, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (used: boolean) =>
      api<Sound>(`/workspaces/${ws}/episodes/${id}/sound`, { method: 'PATCH', body: json({ used_by_listeners: used }) }),
    onSuccess: (sound) => {
      queryClient.setQueryData(soundKey(ws, id), sound)
      refresh(queryClient, ws, id)
    },
  })
}

export function useDiscardEnhanced(ws: string, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api<void>(`/workspaces/${ws}/episodes/${id}/sound`, { method: 'DELETE' }),
    onSuccess: () => refresh(queryClient, ws, id),
  })
}
